import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NotificationTypeEnum } from '@shared/enum/notification-type.enum';
import { NotificationsModel } from '@shared/models/notifications.model';
import { LanguageService } from '@shared/services/language.service';

interface PanelNotificationGroup {
  notification: NotificationsModel;
  count: number;
}

@Component({
    selector: 'app-panel-card',
    templateUrl: './panel-card.component.html',
    styleUrls: ['./panel-card.component.css'],
    standalone: false
})
export class PanelCardComponent {
  @Input() orderStatus : number =0;
  @Input() tableStatus: number =0;
  @Input() tableNumber: string = '';
  @Input() time: string = '';
  @Input() title: string = '';
  @Input() currency: string = '';
  @Input() totalAmount: number = 0;
  @Input() paidAmount: number = 0;
  @Input() hasActiveOrder: boolean = false;
  @Input() orderCreatedTime: string = '';
  @Input() notificationsCount: number = 0;
  @Input() notifications: NotificationsModel[] = [];
  @Output() notificationAction = new EventEmitter<{ action: 'unlock' | 'open' | 'goToTable' | 'done'; notification: NotificationsModel }>();
  notificationsCollapsed = true;

  constructor(public language: LanguageService) {}

  get primaryNotification(): NotificationsModel | null {
    return this.notifications?.length ? this.notifications[0] : null;
  }

  get groupedNotifications(): PanelNotificationGroup[] {
    const groups = new Map<number, PanelNotificationGroup>();

    (this.notifications || []).forEach(notification => {
      const type = Number(notification?.data?.type || 0);
      const repetitionCount = Number(notification?.repetition_count || 0);
      const increment = repetitionCount > 0 ? repetitionCount : 1;
      const existingGroup = groups.get(type);

      if (existingGroup) {
        existingGroup.count += increment;
        return;
      }

      groups.set(type, {
        notification,
        count: increment,
      });
    });

    return Array.from(groups.values());
  }

  get hasNotifications(): boolean {
    return this.notificationsCount > 0 || this.groupedNotifications.length > 0;
  }

  get visibleNotificationsCount(): number {
    return this.notificationsCount || this.groupedNotifications.reduce((total, group) => total + group.count, 0);
  }

  toggleNotifications(event: Event) {
    event.stopPropagation();
    this.notificationsCollapsed = !this.notificationsCollapsed;
  }

  getNotificationAction(notification: NotificationsModel): 'unlock' | 'open' | 'goToTable' | 'done' {
    const type = Number(notification?.data?.type);

    if (type === NotificationTypeEnum.UNLOCK_RESTAURANT_TABLE) {
      return 'unlock';
    }

    if (
      type === NotificationTypeEnum.NEW_ORDER ||
      type === NotificationTypeEnum.NOTIFY_WAITER_TO_UPDATE_ORDER ||
      type === NotificationTypeEnum.NOTIFY_WAITER_TO_PAY_CASH
    ) {
      return 'open';
    }

    if (type === NotificationTypeEnum.NOTIFY_WAITER) {
      return 'goToTable';
    }

    return 'done';
  }

  getNotificationActionLabel(notification: NotificationsModel): string {
    const action = this.getNotificationAction(notification);
    const labels = {
      unlock: 'Unlock table',
      open: 'Open',
      goToTable: 'Go to Table',
      done: 'Done',
    };

    return labels[action];
  }

  handleNotificationAction(event: Event, notification: NotificationsModel) {
    event.stopPropagation();
    this.notificationAction.emit({
      action: this.getNotificationAction(notification),
      notification,
    });
  }

}
